import { ShieldAlert, ShieldCheck } from 'lucide-react';

import { useTableauDeBord } from '@/shared/api/hooksAdmin';
import { entier } from '@/shared/lib/utils';
import { Carte, EnTeteSection, EtatErreur, Tuile } from '@/shared/ui/Table';

/**
 * Vue d'ensemble de la plateforme.
 *
 * L'ETAT DE LA CHAINE D'AUDIT EST LE PREMIER BLOC, avant les chiffres d'activite. Ce n'est
 * pas un choix esthetique : si la chaine est rompue, tout le reste de cet outil perd sa
 * valeur probante, et l'exploitant doit l'apprendre en ouvrant l'application — pas en
 * allant le chercher dans un ecran de reglages.
 */
export default function DashboardPage() {
  const { data, isPending, isError, error, refetch } = useTableauDeBord();

  if (isError) {
    return <EtatErreur erreur={error} onReessayer={() => void refetch()} />;
  }

  const chaineIntacte = data?.chaineAuditIntacte ?? true;

  return (
    <div className="space-y-4">
      <EnTeteSection
        titre="Tableau de bord"
        description="Etat de la plateforme et volumetrie du mois en cours."
      />

      <Carte
        className={
          chaineIntacte
            ? 'flex items-start gap-2 border-succes/30 bg-succes-voile p-3'
            : 'flex items-start gap-2 border-danger/50 bg-danger-voile p-3'
        }
      >
        {chaineIntacte ? (
          <ShieldCheck className="mt-0.5 size-4 shrink-0 text-succes" aria-hidden />
        ) : (
          <ShieldAlert className="mt-0.5 size-4 shrink-0 text-danger" aria-hidden />
        )}

        <div>
          <p className="font-medium text-texte">
            {isPending
              ? 'Verification de la chaine d’audit…'
              : chaineIntacte
                ? 'Chaine d’audit intacte'
                : 'Chaine d’audit rompue'}
          </p>

          <p className="text-texte-doux">
            {chaineIntacte
              ? 'Aucune entree du journal n’a ete alteree ni supprimee.'
              : 'Une entree a ete modifiee ou supprimee directement en base. Consultez le journal d’audit sans attendre.'}
          </p>
        </div>
      </Carte>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Tuile
          libelle="Entreprises actives"
          valeur={isPending ? '…' : entier(data.entreprisesActives)}
        />

        <Tuile
          libelle="Entreprises suspendues"
          valeur={isPending ? '…' : entier(data.entreprisesSuspendues)}
          {...((data?.entreprisesSuspendues ?? 0) > 0 ? { ton: 'alerte' as const } : {})}
        />

        <Tuile libelle="Comptes" valeur={isPending ? '…' : entier(data.comptesActifs)} />

        <Tuile
          libelle="Actes d’administration"
          valeur={isPending ? '…' : entier(data.actesAdminTrenteDerniersJours)}
          detail="30 derniers jours"
        />
      </section>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Tuile libelle="Abonnements actifs" valeur={isPending ? '…' : entier(data.abonnementsActifs)} />

        <Tuile
          libelle="En periode d’essai"
          valeur={isPending ? '…' : entier(data.abonnementsEnEssai)}
        />

        <Tuile
          libelle="Paiements en retard"
          valeur={isPending ? '…' : entier(data.abonnementsEnRetardPaiement)}
          {...((data?.abonnementsEnRetardPaiement ?? 0) > 0 ? { ton: 'danger' as const } : {})}
        />

        <Tuile
          libelle="Appels API"
          valeur={isPending ? '…' : entier(data.appelsApiPeriodeCourante)}
          detail={data?.periodeCourante ?? undefined}
        />
      </section>

      {/*
        Pas de « MRR indicatif ». Le §4 le cite parmi les chiffres cles, mais l'API
        n'expose aucun montant d'abonnement : le calculer ici supposerait de coder une
        grille tarifaire dans le front, ce que le §0.5 interdit — et un chiffre d'affaires
        invente par un client serait pire que pas de chiffre du tout. Il apparaitra quand
        l'API portera le montant du plan.
      */}
      <p className="text-texte-faible">
        Le MRR n’est pas affiche : l’API n’expose pas encore le montant des plans, et le
        calculer ici reviendrait a inventer une verite metier dans le front.
      </p>
    </div>
  );
}
